import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const InvoiceContext = createContext();
 
export const useInvoices = () => {
  const context = useContext(InvoiceContext);
  if (!context) {
    throw new Error('useInvoices must be used within a InvoiceProvider');
  }
  return context;
};

export const InvoiceProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [setApi] = useState(null);
  const { handleApiError } = useError();
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/invoices');

  const getInvoices = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      
      if(filters?.is_full_paid){
        query += '&is_full_paid='+filters?.is_full_paid;
      }  
     
      if(filters?.jobId){
        query += '&jobId='+filters?.jobId;
      } else{
        const jobId = searchParams.get('jobId') || '';
        if(jobId){
          query += '&jobId='+jobId;
        }
      }
      
      let response = await apiInstance.call('invoices?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          query += '&active='+filters?.active;
        } else{
          const active = searchParams.get('active') || '';
          query += '&active='+active;
        }
        if(filters?.jobId){
          query += '&jobId='+filters?.jobId;
        } else{
          const jobId = searchParams.get('jobId') || '';
          if(jobId){
            query += '&jobId='+jobId;
          }
        }
        if(filters?.is_full_paid){
          query += '&is_full_paid='+filters?.is_full_paid;
        }  

        response = await apiInstance.call('invoices?page=' + 0 +''+query);  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setInvoices(response.data.invoices || []);
       
        if(filters){
          return response.data.invoices;
        }
      } else {
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch invoices information'
        });
      }
    } catch (error) {
      // Handle error silently
    } finally {
      // Cleanup if needed
    }
  }, [handleApiError, paginate, getToken]);

  const getInvoiceByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`invoices/${id}/edit`, 'GET', null, true, false);
   
    if (response.status === 200) {
      let invoices = {'data':[response.data.invoice]}
      setInvoices(invoices || []);
    } 
  }, [getToken]);

  useEffect(() => {
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/invoices/edit/'+id))){
        if (getToken()) {
          await getInvoiceByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getInvoiceByIdApi, searchParams, setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
          await getInvoices(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getInvoices, searchParams, initialized, hideLoading, setApi]);

  const getInvoicesAdditionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-invoices-additional-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get invoices data'
      });
    } 
  }, [getToken, handleApiError]);

  const addInvoice = async (data) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }

    try {
      let formData = new FormData();
      
      // Remove any file fields from the original object so we can serialize the rest
      const dataCopy = { ...data };
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('invoices', 'POST', formData, true, true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the invoices list
        await getInvoices();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Invoice'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create invoice'
      });
      return { status: 500, error: error.message };
    }
  };

  const getInvoice = (id) => {
    return invoices?.data?.find(invoice => Number(invoice.id) === Number(id));
  };

  const getInvoiceById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`invoices/${id}/edit`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.invoice };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch invoice details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch invoice details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateInvoice = async (id, updatedData) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }

    try {
      let formData = new FormData();
      
      // Remove any file fields from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      formData.append('_method', 'PUT');

      const response = await apiInstance.call(`invoices/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the invoices list
        await getInvoices();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update invoice'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update invoice'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteInvoice = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`invoices/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the invoices list
        await getInvoices();
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete invoice'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete invoice'
      });
      return { status: 500, error: error.message };
    }
  };

  const value = {
    invoices,
    getInvoices,
    getInvoiceById,
    getInvoice,
    addInvoice,
    createInvoice: addInvoice,
    updateInvoice,
    deleteInvoice,
    getInvoicesAdditionalData,
    isLoading
  };

  return (
    <InvoiceContext.Provider value={value}>
      {children}
    </InvoiceContext.Provider>
  );
};

export default InvoiceContext;
